import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Device } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_DEVICES_QUERY_KEY } from './devices-query-keys';
import { addOrUpdateDeviceCache } from './devices-cache';

/**
 * Every device the caller can see, across every company they belong to. Read
 * once at app start and handed to `DevicesProvider`, which scopes it to the
 * company being viewed.
 */
export function useFetchDevices(): {
    status: 'error' | 'success' | 'pending';
    devices: Device[] | undefined;
    refetch: () => void;
} {
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { status, data, refetch } = useQuery({
        queryKey: [FETCH_DEVICES_QUERY_KEY],
        queryFn: () => {
            return openApiRef.current.devices.getDevices().then((response) => {
                const devices = response.data.data.devices.data;
                // Seed the per-device caches this read already paid for, so
                // opening one device costs no second request.
                devices.forEach((device) => {
                    addOrUpdateDeviceCache(queryClient, device);
                });
                return devices;
            });
        }
    });

    return {
        status,
        devices: data,
        refetch: () => {
            void refetch();
        }
    };
}
